import { IBoardByGroupKey, ITaskCompleteCount, IViewPoint } from "@mctypes/frontend.types";
import { IBoard, IMultiOptionsColors, ITag, IValueAndColor, Paths, PathValue } from "@mctypes/global.types";
import { IMatrixCell, IRisikoCountMatrix, IRisikoCountMatrixConfig, IScoreMatrixConfig } from "@mctypes/issue.types";
import { IAllFilter } from "@schemas/commonSchema";
import { IBoardBucket, IConsequence, IIssueBoardApi, IMapOfEvaluationCategoriesApi, IMapOfRisikoProbabilitiesApi, IMatrixConfig, IMatrixItem, IRisikoProbability } from "@mcapps/shared";
import { IIssueApi, ITableTemplate, ITableTemplateFilter, ITitleAndDescription } from "@schemas/issueSchema";
import { ICapitalLetter } from "@schemas/otherAttributesSchema";
import MiniSearch from "minisearch";
import * as R from "ramda";
import { useRef } from "react";
import { IssueFieldEnum, AccessGroupsEnum, AccessLevelsEnum } from "@mcapps/shared/enums";
import { classifyTidsfrist, getDeeplyNestedValue } from "@mcapps/shared/sharedUtils";
import { countArrByProp, formatTags, sortArrayByNumber } from "../../utils";
import { ISSUE_ACTION_NAME_OPTIONS, ISSUE_BOARD_STATUSES, ISSUE_COMPLETED_VALUE, ISSUE_DUEDATE_OPTIONS, ISSUE_EXPIRED, ISSUE_REGISTER_NAME_OPTIONS, ISSUE_STATES_OPTIONS, IssueKanbanGroupingViewEnum } from "./issueConstants";
import { IIssueDropdownFilters } from "./issueSlice";
import { IKanbanColumn } from "./types";

export const issueBoardHasClientAccess = (issueBoard?: Pick<IIssueBoardApi, "access"> | null): boolean => {
  const access = issueBoard?.access;

  if (access?.level === AccessLevelsEnum.PROJECT) {
    return true;
  }

  if (access?.level === AccessLevelsEnum.GROUPS) {
    return access.groups?.includes(AccessGroupsEnum.CLIENT) === true;
  }

  return false;
};

export const getInitialIssueTableTemplate = (
  tableTemplates: ITableTemplate[],
  defaultTemplateId?: string | null,
  locationSearch = "",
): ITableTemplate | null => {
  const urlTemplateId = new URLSearchParams(locationSearch).get("template");

  return (
    tableTemplates.find((template) => template._id === urlTemplateId) ??
    tableTemplates.find((template) => template._id === defaultTemplateId) ??
    null
  );
};

export const convertIssuesToKanbanBoardColumns = (
  issues: IIssueApi[],
  kanbanGroupingView: IssueKanbanGroupingViewEnum,
  buckets: IBoardBucket[],
): IKanbanColumn<IIssueApi>[] => {
  let arr: ((IValueAndColor | IBoardBucket) & { _id?: string })[] = [];
  let groupKey: "status" | "bucket" | "tidsfrist";

  if (kanbanGroupingView === IssueKanbanGroupingViewEnum.STATUS) {
    arr = ISSUE_STATES_OPTIONS;
    groupKey = "status";
  }

  if (kanbanGroupingView === IssueKanbanGroupingViewEnum.BUCKET) {
    arr = sortArrayByNumber(buckets, "order");

    const validBucketValues = new Set(arr.map((bucket) => bucket.value));
    const hasUnassignedIssues = issues.some(
      (issue) => issue.bucket == null || issue.bucket === "" || validBucketValues.has(issue.bucket) === false,
    );

    if (hasUnassignedIssues === true) {
      arr.push({ value: "No bucket", order: 99, _id: "unassigned" });
    }

    groupKey = "bucket";
  }

  if (kanbanGroupingView === IssueKanbanGroupingViewEnum.TIDSFRIST) {
    arr = ISSUE_DUEDATE_OPTIONS;
    groupKey = "tidsfrist";
  }

  let cardId = 0;

  return arr.map((item, index) => {
    let issuesWithState = [];
    let sortedIssuesWithState = [];

    if (groupKey === "status") {
      issuesWithState = issues.filter((issue) => issue[groupKey] === item.value);
      sortedIssuesWithState = sortArrayByNumber(issuesWithState, "kanbanOrder." + groupKey);
    }

    if (groupKey === "bucket") {
      const validBucketValues = new Set(arr.map((bucket) => bucket.value));

      issuesWithState = issues.filter((issue) => {
        const issueBucket = issue.bucket;
        const isUnassigned =
          item._id === "unassigned" && (!issueBucket || issueBucket === "" || !validBucketValues.has(issueBucket));

        return isUnassigned || issueBucket === item.value;
      });

      sortedIssuesWithState = sortArrayByNumber(issuesWithState, "kanbanOrder." + groupKey);
    }

    if (groupKey === "tidsfrist") {
      issuesWithState = issues.filter((issue) => {
        const dueDate = issue.dates?.due;
        return classifyTidsfrist(dueDate) === item.value;
      });

      sortedIssuesWithState = sortArrayByNumber(issuesWithState, "kanbanOrder." + groupKey);
    }

    const column: IKanbanColumn<IIssueApi> = {
      id: index,
      title: item.value,
      value: item.value,
      cards: sortedIssuesWithState?.map((issue) => {
        return {
          _id: issue._id,
          id: cardId++,
          title: String(issue.id),
          description: issue.title,
          content: issue,
        };
      }),
    };

    return column;
  });
};

export const getTaskCompleteCount = (issue: IIssueApi, darkText = false): ITaskCompleteCount => {
  const allConnectedIssuesAndTasks = [
    ...(issue.connectedTasks || []),
    ...(issue.connectedTasksAndIssuesFromOtherBoards || []),
  ];
  const nonArchivedTasks =
    allConnectedIssuesAndTasks == null
      ? []
      : (allConnectedIssuesAndTasks as IIssueApi[]).filter((task) => task != null && task.archived !== true);
  const count = countArrByProp(nonArchivedTasks, "status", ISSUE_COMPLETED_VALUE);
  const taskHasOverDueDates = nonArchivedTasks.some((task) => {
    const dueDate = task.dates?.due != null ? new Date(task.dates.due) : null;
    return (
      dueDate != null &&
      Number.isNaN(dueDate.getTime()) === false &&
      Date.now() > dueDate.getTime() &&
      task.status !== ISSUE_COMPLETED_VALUE &&
      task.status !== ISSUE_EXPIRED
    );
  });
  return {
    darkText,
    totalNr: nonArchivedTasks.length,
    showWarning: hasUncompletedWithOverdueDates(taskHasOverDueDates, count, nonArchivedTasks.length),
    completedCount: count,
  };
};

const hasUncompletedWithOverdueDates = (hasOverdueDates: boolean, completedNr: number, totalNr: number) => {
  return hasOverdueDates && completedNr !== totalNr;
};

export const hasIssueGisConnection = (issue: IIssueApi) => {
  return issue.gis != null;
};

export const groupBoardsByStatus = (boards: IIssueBoardApi[]): IBoardByGroupKey => {
  const statuses = new Map();

  ISSUE_BOARD_STATUSES.forEach((status) => {
    statuses.set(status.label, []);
  });

  boards
    .filter((b) => b.status.toLocaleLowerCase() !== "deleted")
    .forEach((board) => {
      const status = ISSUE_BOARD_STATUSES.find((status) => status.value === board.status);

      if (status != null) {
        statuses.get(status.label).push(board);
      }
    });

  return Object.fromEntries(statuses);
};

export const addMissingKeysIssue = (
  issue: IIssueApi,
  valueToReplaceWith: PathValue<IIssueApi, Paths<IIssueApi, 4>> = null,
  additionalParameters: string[] = [],
): IIssueApi => {
  const keys: { prop: Paths<IIssueApi, 4>; value: PathValue<IIssueApi, Paths<IIssueApi, 4>> }[] = [
    { prop: "taskOrIssue", value: valueToReplaceWith },
    { prop: "typeOfIssue", value: valueToReplaceWith },
    { prop: "id", value: valueToReplaceWith },
    { prop: "title", value: valueToReplaceWith },
    { prop: "referenceUrl", value: valueToReplaceWith },
    { prop: "pns", value: valueToReplaceWith },
    { prop: "owner", value: valueToReplaceWith },
    { prop: "description", value: valueToReplaceWith },
    { prop: "conclusion", value: valueToReplaceWith },
    { prop: "owner.name", value: valueToReplaceWith },
    { prop: "createdBy.name", value: valueToReplaceWith },
    { prop: "assignedTo.name", value: valueToReplaceWith },
    { prop: "owner.name", value: valueToReplaceWith },
    { prop: "dates.plannedStart", value: "" },
    { prop: "dates.due", value: "" },
    { prop: "dates.completed", value: "" },
    { prop: "lastUpdatedBy.name", value: valueToReplaceWith },
    { prop: "status", value: valueToReplaceWith },
    { prop: "priority", value: valueToReplaceWith },
    { prop: "bucket", value: valueToReplaceWith },
    { prop: "hours.orignalEstimate", value: valueToReplaceWith },
    { prop: "hours.remaining", value: valueToReplaceWith },
    { prop: "hours.completed", value: valueToReplaceWith },
    { prop: "itemMembers", value: valueToReplaceWith },
  ];

  keys.forEach((key) => {
    if (typeof key.prop !== "string") {
      return;
    }

    const splitKeys = key.prop.split(".");

    if (R.path(splitKeys, issue) == null) {
      issue = R.assocPath(splitKeys, key.value, issue);
    }
  });

  additionalParameters.forEach((param) => {
    const splitKeys = ("customAttributeData." + param).split(".");
    if (R.path(splitKeys, issue) == null) {
      issue = R.assocPath(splitKeys, valueToReplaceWith, issue);
    }
  });

  return issue;
};

export const getIssueUrlPath = (historyLocationPathName: string, issueDbId: string) => {
  return `${historyLocationPathName}?id=${issueDbId}`;
};

export const getIssueBoardIdFromUrl = (historyLocationPathName: string) => {
  return historyLocationPathName.split("/")[4];
};

export const pathIsInIssueApp = (historyLocationPathName: string): boolean => {
  return historyLocationPathName.includes("/issues/");
};

export const convertIssuesToViewPoints = (issues: IIssueApi[]): IViewPoint[] => {
  const givenIssuesWithGisPoints = issues.filter((issue) => {
    return issue.gis?.gisPoint?.x != null;
  });

  return givenIssuesWithGisPoints.map((issue) => {
    const state = ISSUE_STATES_OPTIONS.find((opt) => opt.value === issue.status);
    return {
      _id: issue._id,
      gis: issue.gis,
      title: issue.title,
      status: {
        title: state?.value,
        color: state?.color,
      },
    };
  });
};

export const getFiltersWithActiveOption = (allFilters: IAllFilter[]): IAllFilter[] => {
  return (allFilters ?? []).reduce((acc, item) => {
    const hasActiveOption = item.options?.some((opt) => opt.isActive === true);
    if (hasActiveOption === true) {
      let copyOfItem = R.clone(item);
      copyOfItem.options = item.options.filter((opt) => opt.isActive === true);
      acc.push(copyOfItem);
    }
    return acc;
  }, []);
};

export const convertHistoryIssueVal = (val: any) => {
  try {
    const parsed = JSON.parse(val);
    if (parsed.name != null) {
      return parsed.name;
    }
    return val;
  } catch (e) {
    return val;
  }
};

export const createIssueTagFilter = (tags: ITag[]): [{ [key: string]: string }, string[]] => {
  const tagsFormated = formatTags(tags);

  const filterTags = tagsFormated.reduce((acc, tag) => {
    acc[tag] = tag;
    return acc;
  }, {});

  return [filterTags, tagsFormated];
};
export const getIssueRegisterNameOption = (value: string, mode: string) => {
  return ISSUE_REGISTER_NAME_OPTIONS.find((option) => option.type === value)?.[mode];
};

export const getIssueActionNameOption = (value: string, mode: string): string => {
  return ISSUE_ACTION_NAME_OPTIONS.find((option) => option.singular === value)?.[mode];
};

export const formatExcelIssueData = (excel: string[][]): ITitleAndDescription[] => {
  try {
    const titleAndDescriptions: ITitleAndDescription[] = [];
    for (let i = 1; i < excel.length; i++) {
      if (excel[i][0] != null) {
        const row = {
          title: excel[i][0].toString(),
          description: excel[i][1].toString(),
        };
        titleAndDescriptions.push(row);
      }
    }
    return titleAndDescriptions;
  } catch (error) {
    console.error(error);
    alert("Feil i formatering av excel");
  }
};

export const convertValueAndColorToMultiDisplayColor = (arr: IValueAndColor[]): IMultiOptionsColors[] => {
  return arr.map((item) => {
    return {
      displayName: item.value,
      color: item.color,
      key: item.value,
    };
  });
};

interface IGetMatrixArgs {
  issue: IIssueApi;
  issueBoard: IBoard;
  evaluationCategoryCapital: ICapitalLetter;
}

export interface IMatrixOutput {
  matrixItem: IMatrixItem | undefined;
  score: number;
}

type RisikoProbabilityLike = Partial<IRisikoProbability> &
  Pick<IRisikoProbability, "isActive" | "matrixValue" | "order">;

type ConsequenceLike = Partial<IConsequence> & Pick<IConsequence, "isActive" | "matrixValue" | "order">;

type IssueBoardRisikoLike = {
  risikoSection?: {
    probabilites?: Record<string, RisikoProbabilityLike>;
    evaluationCategoriesConfig?: Record<
      string,
      {
        consequences?: Record<string, ConsequenceLike>;
        matrixConfig?: IMatrixConfig;
      }
    >;
  };
};

export const getMatrixConfigFiltred = (
  issueBoard: IssueBoardRisikoLike,
  evaluationCategoryCapital: ICapitalLetter,
): IMatrixConfig => {
  const consequences: IConsequence[] = getConsequencesArr(issueBoard, evaluationCategoryCapital);
  const probabilites: IRisikoProbability[] = getProbabilityArr(issueBoard);
  const matrxConfig: IMatrixConfig =
    issueBoard.risikoSection?.evaluationCategoriesConfig?.[evaluationCategoryCapital].matrixConfig ?? [];

  const matrixFiltredByConsequences = matrxConfig.reduce((acc, matrixConsequence, matrixConsequenceIndex) => {
    const consequence = consequences[matrixConsequenceIndex];
    if (consequence.isActive) {
      acc.push(matrixConsequence);
    }
    return acc;
  }, [] as IMatrixConfig);

  return matrixFiltredByConsequences.map((matrixConsequence) => {
    return matrixConsequence.reduce((acc, matrixProbability, matrixProbabilityIndex) => {
      const probability = probabilites[matrixProbabilityIndex];
      if (probability.isActive) {
        acc.push(matrixProbability);
      }
      return acc;
    }, [] as IMatrixItem[]);
  });
};

export const getConsequencesArr = (
  issueBoard: IssueBoardRisikoLike,
  evaluationCategoryCapital: ICapitalLetter,
): IConsequence[] => {
  const consequences: IConsequence[] = Object.entries(
    issueBoard.risikoSection?.evaluationCategoriesConfig?.[evaluationCategoryCapital]?.consequences ?? {},
  )
    .map(([key, value]) => {
      return {
        ...value,
        id: key,
      } as IConsequence;
    })
    .sort((a, b) => a.order - b.order);

  return consequences;
};

export const getProbabilityArr = (issueBoard: IssueBoardRisikoLike): IRisikoProbability[] => {
  const probabilities: IRisikoProbability[] = Object.entries(issueBoard.risikoSection?.probabilites ?? {})
    .map(([key, value]) => {
      return {
        ...value,
        id: key,
      } as IRisikoProbability;
    })
    .sort((a, b) => a.order - b.order);

  return probabilities;
};

export function buildRisikoCountMatrix(
  issues: IIssueApi[],
  evaluationCategoriesConfig: IMapOfEvaluationCategoriesApi,
  probabilites: IMapOfRisikoProbabilitiesApi,
): IScoreMatrixConfig[] {
  const scoreMatrix: IRisikoCountMatrix = {};
  const matrixConfig: IRisikoCountMatrixConfig = {};

  // make sure evaluationCategoriesConfig is a map, if not convert to map.
  const _evaluationCategoriesConfig = Object.entries(evaluationCategoriesConfig);
  const _probabilites = Object.entries(probabilites);

  _evaluationCategoriesConfig.forEach(([catKey, category]) => {
    if (category.isActive === false) {
      return;
    }

    scoreMatrix[catKey] = {};
    matrixConfig[catKey] = {
      boardName: category.name,
    };

    // make sure category.consequences is a map, if not convert to map.
    let consIndex = 0;
    const consequences = Object.entries(category.consequences);

    consequences.forEach(([conseqKey, consequence]) => {
      if (consequence.isActive === false) {
        return;
      }

      scoreMatrix[catKey][conseqKey] = {};
      matrixConfig[catKey][conseqKey] = {};

      let probIndex = 0;

      _probabilites.forEach(([probKey, _probability], probIndex) => {
        scoreMatrix[catKey][conseqKey][probKey] = { score: 0, issues: [] };
        matrixConfig[catKey][conseqKey][probKey] = getColor(probIndex, consIndex, category.matrixConfig);

        probIndex++;
      });

      consIndex++;
    });
  });

  // Count occurrences for each issue
  for (const issue of issues) {
    if (issue.risiko?.consequence == null) {
      continue;
    }

    const probabilityKey = issue.risiko.probability;
    const _issueConsequence: Map<string, string> =
      issue.risiko.consequence instanceof Map
        ? issue.risiko.consequence
        : new Map(Object.entries(issue.risiko.consequence));

    _issueConsequence.forEach((conseqKey, catKey) => {
      if (
        scoreMatrix[catKey] &&
        scoreMatrix[catKey][conseqKey] &&
        scoreMatrix[catKey][conseqKey][probabilityKey] !== undefined
      ) {
        scoreMatrix[catKey][conseqKey][probabilityKey].score++;
        // This can result in duplicate issues in the array since different
        // matrices can have the same issue. Make sure to halde accordingly.
        scoreMatrix[catKey][conseqKey][probabilityKey].issues.push(issue);
      }
    });
  }

  // Combine matrixConfig and scoreMatrix with issues and make them into arrays
  // Since the object keys are not ordered this happens in two steps

  const matrices: IScoreMatrixConfig[] = [];

  Object.keys(scoreMatrix).forEach((category) => {
    const matrix: IMatrixCell[][] = [];

    Object.entries(scoreMatrix[category]).forEach(([conseqKey, probMatrix]) => {
      const row: IMatrixCell[] = [];

      Object.entries(probMatrix).forEach(([probKey, cellCount]) => {
        row.push({
          score: cellCount.score,
          issues: cellCount.issues,
          color: matrixConfig[category][conseqKey][probKey],
        });
      });

      matrix.push(row);
    });

    matrices.push({ name: matrixConfig[category].boardName, matrix });
  });

  return matrices;
}

// Deduplicate matrices with same shape and colors
export const deduplicateMatrices = (matrices: IScoreMatrixConfig[]): IScoreMatrixConfig[] => {
  const matrixMap = new Map<string, IScoreMatrixConfig>();

  matrices.forEach((matrix) => {
    const key = JSON.stringify(matrix.matrix.map((row) => row.map((cell) => cell.color)));
    const existingMatrix = matrixMap.get(key);

    if (existingMatrix != null) {
      const newMatrix = matrix.matrix.map((row, rowIndex) =>
        row.map((cell, cellIndex) => {
          return {
            score: cell.score + existingMatrix.matrix[rowIndex][cellIndex].score,
            color: cell.color,
            issues: [...cell.issues, ...existingMatrix.matrix[rowIndex][cellIndex].issues],
          };
        }),
      );

      matrixMap.set(key, { name: existingMatrix.name, matrix: newMatrix });
    } else {
      matrixMap.set(key, { ...matrix, name: `Akkumulering ${matrixMap.size + 1}` });
    }
  });

  return Array.from(matrixMap.values());
};

export const getScore = (consequence: IConsequence, probability: IRisikoProbability): number | undefined => {
  return (consequence?.matrixValue ?? 0) * (probability?.matrixValue ?? 0);
};

export const getColor = (probabilityIndex: number, consequenceIndex: number, matrixConfig: IMatrixConfig): string => {
  return matrixConfig?.[consequenceIndex]?.[probabilityIndex]?.color ?? "#ffffff00";
};

export const useDragColumnChange = (cb: (params) => void) => {
  const columnOrderRef = useRef<string[]>([]);
  const onDragStarted = (params) => {
    columnOrderRef.current = params.api.getColumnState().map((c) => c.colId);
  };
  const onDragStopped = (params) => {
    const newColumnOrder = params.api.getColumnState().map((c) => c.colId);
    const sameOrder =
      columnOrderRef.current.length === newColumnOrder.length &&
      columnOrderRef.current.every((c, i) => c === newColumnOrder[i]);
    if (!sameOrder) {
      cb(params);
    }
    // if same order, do nothing
  };

  return { onDragStarted, onDragStopped };
};

// Hjälpfunktion – hålls lokal för läsbarhet
const matchesMineSaker = (issue: IIssueApi, activeValues: string[], currentUserId: string): boolean => {
  return activeValues.some((filterValue) => {
    switch (filterValue) {
      case "owner._id":
        return getDeeplyNestedValue(issue, "owner._id") === currentUserId;
      case "createdBy._id":
        return getDeeplyNestedValue(issue, "createdBy._id") === currentUserId;
      case "member._id":
        const members = getDeeplyNestedValue(issue, "itemMembers");
        return Array.isArray(members) && members.some((m) => getDeeplyNestedValue(m, "_id") === currentUserId);
      case "assigned._id":
        return getDeeplyNestedValue(issue, "assignedTo._id") === currentUserId;
      default:
        return false;
    }
  });
};

export const filterByIssueDropdownFilters = (
  issues: IIssueApi[],
  filters: IIssueDropdownFilters,
  currentUserId: string, // <-- NYTT: krävs för "mine saker"
): IIssueApi[] => {
  if (!filters || Object.keys(filters).length === 0) return issues;

  return issues.filter((issue) => {
    for (const field of Object.keys(filters) as IssueFieldEnum[]) {
      const activeValues = filters[field];
      if (!activeValues || activeValues.length === 0) continue;

      let matches = false;

      // ——— SPECIALHANTERING: MINE_SAKER ———
      if (field === IssueFieldEnum.MINE_SAKER) {
        matches = matchesMineSaker(issue, activeValues, currentUserId);
      }
      // ——— SPECIALHANTERING: TIDSFRIST ———
      else if (field === IssueFieldEnum.DATES_DUE) {
        // HÄMTA STATUS FÖRST
        // FILTRERA BORT FULLFÖRDA OCH UTGÅNDA
        if (issue.status === ISSUE_COMPLETED_VALUE || issue.status === ISSUE_EXPIRED) {
          return false; // Exkludera helt från tidsfrist-filter
        }

        const issueDate = getDeeplyNestedValue(issue, "dates.due");
        const tidsfristClass = classifyTidsfrist(issueDate as string | Date | null);
        matches = activeValues.includes(tidsfristClass);
      }
      // ——— GENERISK LOGIK FÖR ANDRA FÄLT ———
      else {
        const issueValues = getDeeplyNestedValue(issue, field);
        if (issueValues == null) return false;

        if (Array.isArray(issueValues)) {
          matches = activeValues.some((activeValue) => issueValues.some((v) => String(v) === activeValue));
        } else if (typeof issueValues === "object" && issueValues !== null) {
          const id = getDeeplyNestedValue(issueValues, "_id");
          matches = activeValues.includes(String(id));
        } else {
          matches = activeValues.includes(String(issueValues));
        }
      }

      if (!matches) return false;
    }
    return true;
  });
};

// 🆕 FRITEXT-SÖK UTBRUTEN
export const filterByMiniSearch = (
  issues: IIssueApi[],
  miniSearch: MiniSearch<IIssueApi>, // Din globala miniSearch
  freeTextSearch: string,
): IIssueApi[] => {
  if (!freeTextSearch.trim() || !miniSearch) return issues;

  const searchResults = miniSearch.search(freeTextSearch);
  const searchIds = new Set(searchResults.map((r: any) => r.id));
  return issues.filter((issue) => searchIds.has(issue._id));
};

export interface IFilterAllIssuesArgs {
  issues: IIssueApi[];
  filters: IIssueDropdownFilters;
  miniSearch: MiniSearch<IIssueApi> | null;
  freeTextSearch: string;
  pnsFilter: string | null;
  currentUserId: string;
}

export const filterAllIssues = ({
  issues,
  filters,
  miniSearch,
  freeTextSearch,
  pnsFilter,
  currentUserId,
}: IFilterAllIssuesArgs): IIssueApi[] => {
  let filtered = issues;

  // 1️⃣ PNS FILTER FÖRST (snabbt och ofta selektivt)
  if (pnsFilter != null && pnsFilter.trim() !== "") {
    filtered = filtered.filter((issue) => {
      if (issue.pns == null || issue.pns.trim() === "") return false;
      return issue.pns.startsWith(pnsFilter);
    });
  }

  // 2️⃣ SEDAN DROPDOWNS
  filtered = filterByIssueDropdownFilters(filtered, filters, currentUserId);

  // 3️⃣ SEDAN FRITEXT (sist, eftersom det kan vara dyrare)
  filtered = filterByMiniSearch(filtered, miniSearch as MiniSearch<IIssueApi>, freeTextSearch);

  return filtered;
};

export const convertIssueDropdownFiltersToTableTemplateFilter = (
  issueDropdownFilters: IIssueDropdownFilters,
): ITableTemplateFilter[] => {
  const tableTemplateFilters: ITableTemplateFilter[] = [];
  for (const field of Object.keys(issueDropdownFilters) as IssueFieldEnum[]) {
    const activeValues = issueDropdownFilters[field];
    if (activeValues != null && activeValues.length > 0) {
      tableTemplateFilters.push({
        typeOfFilter: "MULTIFILTER",
        dataField: field,
        multiFilterValues: activeValues,
      });
    }
  }
  return tableTemplateFilters;
};

export const convertTableTemplateFilterToIssueDropdownFilters = (
  tableTemplateFilters: ITableTemplateFilter[],
): IIssueDropdownFilters => {
  const issueDropdownFilters: IIssueDropdownFilters = {};
  for (const filter of tableTemplateFilters) {
    if (filter.typeOfFilter !== "BOOLEANFILTER" && filter.dataField && filter.multiFilterValues?.length > 0) {
      const activeValues = filter.multiFilterValues.filter((value): value is string => typeof value === "string");
      if (activeValues.length > 0) {
        issueDropdownFilters[filter.dataField] = activeValues;
      }
    }
  }
  return issueDropdownFilters;
};
